import { useEffect, useRef, useState } from "react";

interface Turnstile {
  render(
    element: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  remove(id: string): void;
}
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let loading: Promise<void> | undefined;
function loadChallenge(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.onload = () => resolve();
    script.onerror = () => {
      loading = undefined;
      script.remove();
      reject(new Error("Verification unavailable"));
    };
    document.head.append(script);
  });
  return loading;
}

export function BookingChallenge({
  siteKey,
  onToken,
}: {
  readonly siteKey: string;
  readonly onToken: (token: string) => void;
}) {
  const element = useRef<HTMLDivElement>(null);
  const callback = useRef(onToken);
  callback.current = onToken;
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let widget: string | undefined;
    callback.current("");
    void loadChallenge()
      .then(() => {
        if (!active || !element.current || !window.turnstile) return;
        widget = window.turnstile.render(element.current, {
          sitekey: siteKey,
          action: "appointment_booking",
          callback: (token) => {
            if (active) callback.current(token);
          },
          "expired-callback": () => {
            if (active) callback.current("");
          },
          "error-callback": () => {
            if (active) {
              callback.current("");
              setFailed(true);
            }
          },
        });
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (widget !== undefined) window.turnstile?.remove(widget);
    };
  }, [siteKey]);
  return (
    <section aria-label="Booking verification">
      <div ref={element} />
      {failed ? (
        <p role="alert">Verification is unavailable. Reload this page or contact the clinic.</p>
      ) : null}
    </section>
  );
}
